<template>
  <BaseModal :name="USER_PROFILE_COMPLETE_MODAL_NAME" width-class="max-w-2xl">
    <div class="relative flex w-full flex-col items-center p-8">
      <div class="relative">
        <Avatar
          :src="currentUser?.avatarUrl"
          size="2xl"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        />
        <img :src="DealsCodeBackground" alt="" class="w-96" />
      </div>

      <div class="heading-1 mt-16">Profile Complete</div>
      <div class="mt-4 w-full max-w-sm text-center text-sm text-neutral-500">
        <p>Your GrowthMentor profile looks amazing!</p>
        <p>You're all set to start your growth journey.</p>
      </div>

      <div
        class="border-secondary-100 mt-8 w-full rounded-xl border p-6 text-center"
        style="
          background: radial-gradient(
            65.59% 100% at 50% 0%,
            #e6d8ff 0%,
            #ffffff 100%
          );
        "
      >
        <div class="text-secondary-800 text-lg font-semibold">
          Share GrowthMentor with your network
        </div>
        <p class="text-secondary-800 mt-1 text-sm">
          Know people who could benefit from mentorship? Share your link. They
          get a free month with a session in it, and you get
          {{ currentUser.referral.commission }}% of what they pay for a year.
        </p>
        <FormInput type="text" readonly :model-value="inviteLink" class="mt-4">
          <template #input-append>
            <BaseButton
              @click="copyReferralLink"
              color="outline-light-purple"
              class="shrink-0 rounded-lg py-2.5"
            >
              <CopyIcon class="mr-2 hidden h-4 w-4 sm:block" />
              Copy
            </BaseButton>
          </template>
        </FormInput>
      </div>

      <div class="mt-8 w-full">
        <BaseButton
          v-if="hasRole(ROLE.MENTEE)"
          @click="closeModal(USER_PROFILE_COMPLETE_MODAL_NAME)"
          href="/opportunities/new"
          is-inertia-link
          color="primary"
          is-block
          size="large"
        >
          Post a Help Request
        </BaseButton>

        <BaseButton
          v-if="hasRole(ROLE.MENTOR)"
          @click="closeModal(USER_PROFILE_COMPLETE_MODAL_NAME)"
          href="/opportunities"
          is-inertia-link
          color="primary"
          is-block
          size="large"
        >
          Browse Help Requests
        </BaseButton>
      </div>

      <IconButton
        class="absolute top-4 right-4"
        @click="closeModal(USER_PROFILE_COMPLETE_MODAL_NAME)"
      >
        <CloseIcon />
      </IconButton>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import CopyIcon from '~icons/lucide/copy'
import BaseModal from '@/shared/components/BaseModal.vue'
import { useActionCable } from '@/shared/composables/useActionCable'
import { useModals } from '@/shared/composables/useModals'
import { useDebugger } from '@/shared/composables/useDebugger'
import { USER_PROFILE_COMPLETE_MODAL_NAME } from '@/shared/data/globalVariables.js'
import { PROFILE_CHANNEL } from '@/shared/data/actionCableChannels.js'
import DealsCodeBackground from '@/assets/images/deals-code-background.png'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import { ROLE } from '@/shared/data/permissionSystem'
import FormInput from '@/shared/components/forms/FormInput.vue'
import { useClipboard } from '@vueuse/core'
import { notify } from '@kyvg/vue3-notification'
import IconButton from '@/shared/components/IconButton.vue'
import CloseIcon from '~icons/lucide/x'

const { currentUser } = useCurrentUser()
const { hasRole } = usePermissionSystem()
const { openModal, closeModal } = useModals()
const { $debug } = useDebugger()

const { subscribeChannel } = useActionCable({
  channelName: PROFILE_CHANNEL,
  onConnected: () => {
    $debug(`[AC Channel: ${PROFILE_CHANNEL}] connected`)
  },
  onReceived: ((data: { event: string }) => {
    $debug(
      `[AC Channel: ${PROFILE_CHANNEL}] received data: ${JSON.stringify(data)}`,
    )
    if (data.event === 'profile_completed') {
      $debug(
        `[AC Channel: ${PROFILE_CHANNEL}] Profile completed, opening modal`,
      )
      openModal(USER_PROFILE_COMPLETE_MODAL_NAME)
    }
  }) as (data: unknown) => void,
  onDisconnected: () => {
    $debug(`[AC Channel: ${PROFILE_CHANNEL}] disconnected`)
  },
})

onMounted(() => {
  subscribeChannel()
})

const { copy } = useClipboard()

const inviteLink = computed(() => {
  return currentUser.value.referral.url
})

const copyReferralLink = () => {
  copy(inviteLink.value)
  notify({
    type: 'success',
    text: 'Link copied.',
  })
}
</script>
